import SharedDialog from '../../../../shared/shared-dialog';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../state/zustand-store';
import {
  saveScheduledUserStatusUpdate,
  UserKeys,
} from '../../../../api/users-api';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { ModuleList } from '../../../../shared/analytics/module-list';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { useAnalytics } from '../../../../shared/analytics/create-events';

import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface ScheduledUserStatusUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  userStatus: string;
  username: string;
  userRoles: string[];
  user: { primaryEmail: string };
  primaryDomain: string;
  changeLogRoles: string[];
}

export default function ScheduledUserStatusUpdateDialog({
  open,
  onClose,
  userStatus,
  username,
  userRoles,
  user,
  primaryDomain,
  changeLogRoles,
}: ScheduledUserStatusUpdateDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const nextHour = dayjs().add(1, 'hour').startOf('hour');
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();

  const onSave = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.ScheduleUserStatusUpdate.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (!selectedDate) throw new Error('No date selected');
      return saveScheduledUserStatusUpdate(
        {
          scheduledDatetime: selectedDate.valueOf(),
          newStatus: selectedStatus,
          userEmail: user.primaryEmail,
          primaryDomain,
        },
        changeLogMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Scheduled(
          user.primaryEmail + "'s status update"
        ),
      });
      queryClient.setQueryData(
        [UserKeys.ScheduledUserStatusUpdate, user.primaryEmail],
        {
          scheduledDatetime: selectedDate?.valueOf(),
          newStatus: selectedStatus,
        }
      );
      onClose();
    },
    onError: () => {
      setSnackbar({
        open: true,
        severity: 'error',
        message: Messages.Errors.Network.Generic,
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.create_scheduled_status,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.userDetails,
          newStatus: selectedStatus,
          timeInAdvance:
            (selectedDate ? selectedDate.valueOf() : 0) - Date.now(),
        }
      );
    },
  });

  const isDateInvalid = !selectedDate || !selectedDate.isValid();
  const isSaveDisabled =
    selectedStatus === '' ||
    selectedStatus === userStatus ||
    isDateInvalid ||
    isPending;

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSelectedStatus('');
        setSelectedDate(null);
        onClose();
      }}
      title="Schedule Status Change"
      dialogContent={
        <Stack gap={2}>
          <Typography>
            Schedule status change for <strong>{username}</strong>.
          </Typography>

          <Stack flexDirection={'row'} gap={2}>
            <TextField
              autoFocus
              select
              label="Status"
              value={
                ['active', 'suspended', 'archived'].includes(selectedStatus)
                  ? selectedStatus
                  : ''
              }
              onChange={(e) => {
                setSelectedStatus(e.target.value);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem
                value={'active'}
                disabled={
                  !isAbleToSeeUIElement(
                    userRoles,
                    UserRoles.ActivateUsers.value
                  )
                }
              >
                Active
              </MenuItem>
              <MenuItem
                value={'suspended'}
                disabled={
                  !isAbleToSeeUIElement(userRoles, UserRoles.SuspendUsers.value)
                }
              >
                Suspended
              </MenuItem>
              <MenuItem
                value={'archived'}
                disabled={
                  !isAbleToSeeUIElement(userRoles, UserRoles.ArchiveUsers.value)
                }
              >
                Archived
              </MenuItem>
            </TextField>

            <DateTimePicker
              label="Date"
              views={['year', 'month', 'day', 'hours']}
              value={selectedDate}
              timezone="system"
              disablePast
              minDateTime={nextHour}
              onChange={(e) => {
                setSelectedDate(e);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate();
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        action: () => onSave(),
        disabled: isSaveDisabled,
        title: 'Schedule Change',
        loading: isPending,
      }}
    />
  );
}
